import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, model, signal, untracked } from '@angular/core';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';

import { IconComponent } from '../../../../shared/icon/icon.component';

const WEEK_OPTIONS = { weekStartsOn: 1 } as const;

interface CalendarDay {
  date: Date;
  label: string;
  disabled: boolean;
  cellClass: string;
  buttonClass: string;
}

const dayButtonClass = ({ inMonth, disabled, isEdge, isToday }: {
  inMonth: boolean;
  disabled: boolean;
  isEdge: boolean;
  isToday: boolean;
}): string => {
  if (isEdge) return 'bg-[#0A84FF] text-white font-semibold';
  if (disabled) return 'text-gray-300 cursor-not-allowed';
  const color = isToday ? 'text-[#0A84FF] font-semibold' : inMonth ? 'text-gray-900' : 'text-gray-300';
  return `${color} hover:bg-gray-100`;
};

@Component({
  selector: 'app-date-range-calendar',
  imports: [IconComponent],
  templateUrl: './date-range-calendar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangeCalendarComponent {
  readonly from = model<Date | null>(null);
  readonly to = model<Date | null>(null);
  readonly min = input.required<Date>();
  readonly max = input.required<Date>();

  // Opens on the selected end month; afterwards only the month arrows move it, so selecting
  // days never swaps the grid under the cursor.
  protected readonly visibleMonth = linkedSignal(() => startOfMonth(untracked(this.to) ?? this.max()));
  protected readonly hoverDate = signal<Date | null>(null);

  protected readonly monthLabel = computed(() => format(this.visibleMonth(), 'MMMM yyyy'));
  protected readonly canGoBack = computed(() => isAfter(this.visibleMonth(), startOfMonth(this.min())));
  protected readonly canGoForward = computed(() => isBefore(this.visibleMonth(), startOfMonth(this.max())));

  protected readonly weekdays = eachDayOfInterval({
    start: startOfWeek(new Date(), WEEK_OPTIONS),
    end: endOfWeek(new Date(), WEEK_OPTIONS),
  }).map(day => format(day, 'EEEEEE'));

  // While only a start date is picked, the hovered day previews the end of the range.
  private readonly previewRange = computed(() => {
    const from = this.from();
    const to = this.to() ?? (from && this.hoverDate());
    if (!from || !to) return null;
    return isBefore(to, from) ? { start: to, end: from } : { start: from, end: to };
  });

  protected readonly days = computed<CalendarDay[]>(() => {
    const month = this.visibleMonth();
    const range = this.previewRange();
    const from = this.from();

    return eachDayOfInterval({
      start: startOfWeek(month, WEEK_OPTIONS),
      end: endOfWeek(endOfMonth(month), WEEK_OPTIONS),
    }).map(date => {
      const isStart = range ? isSameDay(date, range.start) : !!from && isSameDay(date, from);
      const isEnd = !!range && isSameDay(date, range.end);
      const inRange = !!range && !isSameDay(range.start, range.end) && isWithinInterval(date, range);
      const disabled = isBefore(date, this.min()) || isAfter(date, this.max());

      return {
        date,
        label: format(date, 'd'),
        disabled,
        cellClass: [
          inRange ? 'bg-blue-50' : '',
          inRange && isStart ? 'rounded-l-full' : '',
          inRange && isEnd ? 'rounded-r-full' : '',
        ].join(' '),
        buttonClass: dayButtonClass({
          inMonth: isSameMonth(date, month),
          disabled,
          isEdge: isStart || isEnd,
          isToday: isToday(date),
        }),
      };
    });
  });

  protected previousMonth(): void {
    this.visibleMonth.update(month => subMonths(month, 1));
  }

  protected nextMonth(): void {
    this.visibleMonth.update(month => addMonths(month, 1));
  }

  protected select(day: CalendarDay): void {
    if (day.disabled) return;

    const from = this.from();
    if (!from || this.to()) {
      this.from.set(day.date);
      this.to.set(null);
    } else if (isBefore(day.date, from)) {
      this.from.set(day.date);
      this.to.set(from);
    } else {
      this.to.set(day.date);
    }
  }
}
